import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

/**
 * Kleine gemeinsame Bausteine des Bestellstrecken-Dialogs (B5): Bestaetigungsdialog, Zeitformat,
 * Lade-Platzhalter. Bewusst ohne Datenzugriff.
 */

/**
 * Berliner Wallclock fuer die Anzeige: 'YYYY-MM-DD HH:mm:ss' → 'DD.MM.YYYY HH:mm'.
 *
 * Kommt der Wert als ISO-String an (mysql2-Date, JSON-serialisiert), traegt er die Berliner Bytes
 * UTC-markiert (CLAUDE.md § Timezones) — dann ebenfalls nur Text abschneiden, NIE `new Date()`
 * (das wuerde im Browser um 1–2 h verschieben).
 */
export function formatBerlin(value: string | null | undefined): string {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value);
  if (!m) return value;
  return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`;
}

const BERLIN_FMT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/**
 * Fuer Spalten mit ECHTEN UTC-Bytes (z. B. lp_webinar_pages.updated_at: ON UPDATE
 * CURRENT_TIMESTAMP in einer sequelize-Sitzung, die auf +00:00 steht) → Berliner Anzeige
 * 'DD.MM.YYYY HH:mm'. Anders als formatBerlin wird hier wirklich umgerechnet; ein nackter
 * 'YYYY-MM-DD HH:mm:ss' gilt ebenfalls als UTC. Die Koordinate ist je SPALTE festgelegt — nur
 * benutzen, wenn der Schreiber der Spalte nachweislich UTC schreibt.
 */
export function formatUtcAsBerlin(value: string | null | undefined): string {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(value);
  if (!m) return value;
  const ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], m[6] ? +m[6] : 0);
  const teile: Record<string, string> = {};
  for (const p of BERLIN_FMT.formatToParts(new Date(ms))) teile[p.type] = p.value;
  return `${teile.day}.${teile.month}.${teile.year} ${teile.hour}:${teile.minute}`;
}

export const shortHash = (h: string | null | undefined) => (h ? h.slice(0, 10) : '—');

export function Loading({ label }: { label?: string }) {
  return (
    <Box p={3} display="flex" alignItems="center" justifyContent="center" gap={1.5}>
      <CircularProgress size={22} />
      {label && (
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      )}
    </Box>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  children?: React.ReactNode;
  confirmLabel: string;
  /** Rot = destruktiv / umgeht eine Warnung. */
  danger?: boolean;
  busy?: boolean;
  /** Zusatzriegel, z. B. getippte Bestaetigung. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  danger,
  busy,
  confirmDisabled,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      {children && <DialogContent dividers>{children}</DialogContent>}
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          color={danger ? 'error' : 'primary'}
          onClick={onConfirm}
          disabled={busy || confirmDisabled}
        >
          {busy ? 'Läuft …' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Hinweise (Sanitizer-/Publish-Warnungen) als Liste — nie verwerfen (Spec § Files to touch). */
export function WarningList({ warnings }: { warnings: string[] }) {
  if (!warnings.length) return null;
  return (
    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
      {warnings.map((w, i) => (
        <li key={`${i}-${w}`}>
          <Typography variant="body2">{w}</Typography>
        </li>
      ))}
    </Box>
  );
}
